import {
    createSelector,
    HubListEntryKind,
    PersonType,
    joinedUnifiedGroupsLeftNavId,
    ownedUnifiedGroupsLeftNavId,
    deletedUnifiedGroupsLeftNavId,
} from '../../../shared';
import {
    AppState,
    UnifiedGroupsMap,
    ClickedHubPersonaState,
    isInSearchMode,
    getActiveLeftNavItemId,
    isInDeepLinkMode,
} from '../../../appstate';
import type { UnifiedGroup } from '../../actions/user/groups/unifiedGroupListActions';
import {
    getFirstUnifiedGroupIdInList,
    UnifiedGroupListEntry,
    getLoadingState,
    getGroupedUnifiedGroupListEntries,
    UnifiedGroupEntry,
} from './unifiedGroupListSelector';
import type { HubState } from '../../state';
import type { GroupsHubListLoadingViewState } from '../../state/groupsState';
import { getCurrentHubPersonas } from './../currentPersonaSelector';

/**
 * Returns a UnifiedGroupsId, for the Unifiedgroups currently in focus.
 */
export const getCurrentUnifiedGroupId = createSelector<
    AppState,
    HubState,
    ClickedHubPersonaState,
    UnifiedGroupsMap,
    string,
    string
>(
    'getCurrentUnifiedGroupId',
    [
        (appState: AppState) => appState.view.navigationState.clickedHubPersona,
        (appState: AppState) => appState.data.groupsHubState.unifiedGroupsMap,
        getFirstUnifiedGroupIdInList,
    ],

    (clickedHubPersona, groupsMap, firstUnifiedGroupId) => {
        if (clickedHubPersona) {
            const unifiedGroups = Object.keys(groupsMap)
                .map(groupId => groupsMap[groupId])
                .filter(group => group.hubPersonaId === clickedHubPersona.hubPersonaId);
            return unifiedGroups?.[0]?.hubPersonaId;
        }

        return firstUnifiedGroupId;
    }
);

/**
 * Returns one UnifiedGroup, indicating the Group currently in focus.
 */
export const getCurrentUnifiedGroup = createSelector<
    AppState,
    HubState,
    AppState,
    HubState,
    string,
    UnifiedGroupsMap,
    string,
    boolean,
    UnifiedGroup
>(
    'getCurrentUnifiedGroup',
    [
        appState => appState,
        (_appState, hubState) => hubState,
        getCurrentUnifiedGroupId,
        (appState: AppState) => appState.data.groupsHubState.unifiedGroupsMap,
        getActiveLeftNavItemId,
        isInDeepLinkMode,
    ],
    (
        appState,
        hubState,
        currentUnifiedGroupId,
        unifiedGroupsMap,
        activeLeftNavId,
        isInDeepLinkMode
    ) => {
        if (
            unifiedGroupsMap?.[currentUnifiedGroupId] &&
            (activeLeftNavId === joinedUnifiedGroupsLeftNavId ||
                activeLeftNavId === ownedUnifiedGroupsLeftNavId ||
                activeLeftNavId === deletedUnifiedGroupsLeftNavId ||
                isInDeepLinkMode) &&
            !appState.view.navigationState.currentQuery
        ) {
            return unifiedGroupsMap[currentUnifiedGroupId];
        }

        const currentHubPersonas = getCurrentHubPersonas(appState, hubState);
        if (
            currentHubPersonas.length > 0 &&
            (currentHubPersonas[0].type === PersonType.Group ||
                currentHubPersonas[0].type === PersonType.GroupBasicData ||
                currentHubPersonas[0].type === PersonType.FavoriteUnifiedGroup)
        ) {
            let currentGroup: UnifiedGroup;
            if (unifiedGroupsMap) {
                const unifiedgroups = Object.keys(unifiedGroupsMap)
                    .map(groupId => unifiedGroupsMap[groupId])
                    .filter(group => group.hubPersonaId === currentHubPersonas[0].hubPersonaId);
                currentGroup = unifiedgroups.length > 0 ? unifiedgroups[0] : undefined;
            }

            return currentGroup ? currentGroup : (currentHubPersonas[0] as UnifiedGroup);
        }

        return null;
    }
);

/*
 * Returns the next item from the groups list
 */
export const getNextUnifiedGroupIdInList = createSelector<
    AppState,
    HubState,
    boolean,
    string,
    GroupsHubListLoadingViewState,
    UnifiedGroupListEntry[],
    string
>(
    'getNextUnifiedGroupIdInList',
    [isInSearchMode, getCurrentUnifiedGroupId, getLoadingState, getGroupedUnifiedGroupListEntries],
    (isInSearchMode, currentGroupId, loadingState, unifiedGroupListEntries) => {
        if (isInSearchMode || !unifiedGroupListEntries || loadingState !== 'results') {
            return null;
        }
        const groupListElements = unifiedGroupListEntries.filter(
            entry => entry.kind === HubListEntryKind.Content
        ) as UnifiedGroupEntry[];
        const currentIndex = groupListElements.findIndex(element => {
            return element.hubPersonaId === currentGroupId;
        });

        // Return next item in the list and in case if the currentIndexed item is last one in the list then return the one above it
        return groupListElements[currentIndex + 1]
            ? groupListElements[currentIndex + 1].hubPersonaId
            : groupListElements[currentIndex - 1]
            ? groupListElements[currentIndex - 1].hubPersonaId
            : undefined;
    }
);

export const getIsInGroups = createSelector<AppState, HubState, string, boolean>(
    'getIsInGroups',
    [getActiveLeftNavItemId],
    activeLeftNavId => {
        return (
            activeLeftNavId === joinedUnifiedGroupsLeftNavId ||
            activeLeftNavId === ownedUnifiedGroupsLeftNavId ||
            activeLeftNavId === deletedUnifiedGroupsLeftNavId
        );
    }
);
